import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trainingsService } from '../../services/trainings.service';
import paymentReceiptsService from '../../services/payment-receipts.service';
import { 
  Crown, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Upload, 
  FileText, 
  AlertCircle,
  Star,
  Zap,
  Shield,
  Users,
  Calendar,
  DollarSign
} from 'lucide-react';

interface Plan {
  id: number;
  title: string;
  duration: string;
  price: number;
  listing_limit: number | null;
  features?: string[];
}

interface CurrentSubscription {
  id: string;
  plan?: {
    title: string;
    price: number;
    listing_limit: number | null;
  };
  subscription_plan?: {
    title: string;
    price: number;
    listing_limit: number | null;
  };
  status: 'active' | 'expired' | 'pending' | 'rejected';
  start_date?: string;
  end_date?: string;
  startDate?: string;
  endDate?: string;
  created_at: string;
  published_services?: number;
  remaining_slots?: number | null;
}

const ConsultantSubscriptionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [serviceStats, setServiceStats] = useState<{ total: number; active: number; completed: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [receipt, setReceipt] = useState<File | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Try to get plans and stats first
      const [plansData, statsData] = await Promise.all([
        trainingsService.getSubscriptionPlans(),
        getConsultantServiceStats().catch(() => null)
      ]);
      
      setPlans(plansData);
      setServiceStats(statsData);
      
      // Get subscription data from the working endpoint
      let subscriptionData = null;
      
      try {
        // Use the payment receipts endpoint which is working
        const mySubscriptions = await paymentReceiptsService.getMySubscriptions();
        
        if (mySubscriptions && mySubscriptions.length > 0) {
          // Get the most recent subscription (first one since they're ordered by DESC)
          const latestSubscription = mySubscriptions[0];
          
          // Transform the data to match our interface
          subscriptionData = {
            id: String(latestSubscription.id),
            plan: {
              title: latestSubscription.subscription_plan?.title || 'Plan inconnu',
              price: latestSubscription.subscription_plan?.price || 0,
              listing_limit: (latestSubscription.subscription_plan as any)?.listing_limit || (latestSubscription.subscription_plan as any)?.listingLimit || null
            },
            subscription_plan: {
              title: latestSubscription.subscription_plan?.title || 'Plan inconnu',
              price: latestSubscription.subscription_plan?.price || 0,
              listing_limit: (latestSubscription.subscription_plan as any)?.listing_limit || (latestSubscription.subscription_plan as any)?.listingLimit || null
            },
            status: latestSubscription.status,
            start_date: (latestSubscription as any).start_date || latestSubscription.created_at,
            end_date: (latestSubscription as any).end_date || ((latestSubscription as any).start_date ? 
              new Date(new Date((latestSubscription as any).start_date).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString() : 
              new Date(new Date(latestSubscription.created_at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString()
            ),
            startDate: (latestSubscription as any).start_date || latestSubscription.created_at,
            endDate: (latestSubscription as any).end_date || ((latestSubscription as any).start_date ? 
              new Date(new Date((latestSubscription as any).start_date).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString() : 
              new Date(new Date(latestSubscription.created_at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString()
            ),
            created_at: latestSubscription.created_at || (latestSubscription as any).start_date
          };
        }
      } catch (error) {
        // Silent error handling
      }
      
      setCurrentSubscription(subscriptionData);
      
      // Calculate remaining slots if subscription is active
      if (subscriptionData && subscriptionData.status === 'active' && statsData) {
        const publishedCount = statsData.total;
        const limit = subscriptionData.plan?.listing_limit || subscriptionData.subscription_plan?.listing_limit;
        const remaining = limit ? Math.max(0, limit - publishedCount) : null;
        
        const updatedSubscription: CurrentSubscription = {
          ...subscriptionData,
          published_services: publishedCount,
          remaining_slots: remaining
        };
        
        setCurrentSubscription(updatedSubscription);
      }
    } catch (e) {
      setMessage({ type: 'error', text: t('subscriptions.messages.dataLoadError') });
    } finally {
      setLoading(false);
    }
  };

  const getConsultantServiceStats = async () => {
    // This would be replaced with actual consultant service stats API
    return { total: 0, active: 0, completed: 0 };
  };

  const submitRequest = async () => {
    if (!selectedPlan || !receipt) {
      setMessage({ type: 'error', text: t('subscriptions.messages.selectPlanAndReceipt') });
      return;
    }
    
    try {
      setUploading(true);
      await trainingsService.submitSubscriptionRequest(selectedPlan, receipt);
      setMessage({ 
        type: 'success', 
        text: t('subscriptions.messages.receiptSentSuccess')
      });
      setReceipt(null);
      setSelectedPlan(null);
      // Reload data to show updated subscription status
      setTimeout(() => loadData(), 1000);
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.message || t('subscriptions.messages.receiptSendError') });
    } finally {
      setUploading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'pending': return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'rejected': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'expired': return <AlertCircle className="h-5 w-5 text-orange-500" />;
      default: return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return t('subscriptions.status.active');
      case 'pending': return t('subscriptions.status.pending');
      case 'rejected': return t('subscriptions.status.rejected');
      case 'expired': return t('subscriptions.status.expired');
      default: return t('subscriptions.status.unknown');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'expired': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('subscriptions.title')}</h1>
          <p className="text-gray-600 mt-1">{t('subscriptions.subtitleConsultant')}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Crown className="h-6 w-6 text-brand-primary" />
          <span className="text-sm font-medium text-gray-700">{t('subscriptions.consultant')}</span>
        </div>
      </div>

      {/* Current Subscription Status */}
      {(currentSubscription || (serviceStats && serviceStats.total > 1)) && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-brand-primary/10 rounded-lg">
                <Crown className="h-6 w-6 text-brand-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('subscriptions.currentSubscription')}</h3>
                <p className="text-gray-600">
                  {currentSubscription ? (
                    currentSubscription.plan?.title || 
                    currentSubscription.subscription_plan?.title || 
                    t('subscriptions.unknownPlan')
                  ) : (
                    t('subscriptions.activePlan')
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className={`px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(currentSubscription?.status || 'active')}`}>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(currentSubscription?.status || 'active')}
                  <span>{getStatusText(currentSubscription?.status || 'active')}</span>
                </div>
              </div>
              {(currentSubscription?.status === 'active' || !currentSubscription) && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">{t('subscriptions.expiresOn')}</p>
                  <p className="text-sm font-medium">
                    {currentSubscription ? (
                      new Date(currentSubscription.end_date || currentSubscription.endDate || '').toLocaleDateString('fr-FR')
                    ) : (
                      t('subscriptions.notAvailable')
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Detailed Subscription Information */}
          {(currentSubscription?.status === 'active' || (!currentSubscription && serviceStats && serviceStats.total > 1)) && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="h-5 w-5 text-brand-primary" />
                  <span className="text-sm font-medium text-gray-700">{t('subscriptions.startDate')}</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {currentSubscription ? (
                    new Date(currentSubscription.start_date || currentSubscription.startDate || '').toLocaleDateString('fr-FR')
                  ) : (
                    t('subscriptions.notAvailable')
                  )}
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="h-5 w-5 text-brand-secondary" />
                  <span className="text-sm font-medium text-gray-700">{t('subscriptions.endDate')}</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {currentSubscription ? (
                    new Date(currentSubscription.end_date || currentSubscription.endDate || '').toLocaleDateString('fr-FR')
                  ) : (
                    t('subscriptions.notAvailable')
                  )}
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">{t('subscriptions.publishedServices')}</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {currentSubscription?.published_services || serviceStats?.total || 0}
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">
                    {(currentSubscription?.plan?.listing_limit || currentSubscription?.subscription_plan?.listing_limit) ? t('subscriptions.remainingServices') : t('subscriptions.unlimited')}
                  </span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {(currentSubscription?.plan?.listing_limit || currentSubscription?.subscription_plan?.listing_limit)
                    ? (currentSubscription?.remaining_slots !== null ? currentSubscription.remaining_slots : t('subscriptions.notAvailable'))
                    : '∞'
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-800 border-red-200' :
          'bg-blue-50 text-blue-800 border-blue-200'
        }`}>
          <div className="flex items-center space-x-2">
            {message.type === 'success' && <CheckCircle className="h-5 w-5" />}
            {message.type === 'error' && <XCircle className="h-5 w-5" />}
            {message.type === 'info' && <AlertCircle className="h-5 w-5" />}
            <span>{message.text}</span>
          </div>
        </div>
      )}

       {/* Free Plan Info */}
       <div className="bg-green-50 border border-green-200 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-green-900">{t('subscriptions.gratuits')}</h3>
            <p className="text-green-700 mt-1">{t('subscriptions.fevrier2026')}
             
            </p>
          </div>
        </div>
      </div>

      {/* Subscription Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan, index) => {
          const isSelected = selectedPlan === plan.id;
          const isPopular = plan.title.toLowerCase().includes('pro') || plan.title.toLowerCase().includes('premium');
          const hasActiveSubscription = currentSubscription?.status === 'active';
          const isCurrentPlan = currentSubscription && (
            currentSubscription.plan?.title === plan.title || 
            currentSubscription.subscription_plan?.title === plan.title
          );
          
          // Define plan colors and icons - generic styling for annual plans
          const getPlanStyle = (index: number) => {
            const colors = [
              { bgColor: 'bg-blue-50', borderColor: 'border-blue-200', selectedBg: 'bg-blue-100', selectedBorder: 'border-blue-400' },
              { bgColor: 'bg-purple-50', borderColor: 'border-purple-200', selectedBg: 'bg-purple-100', selectedBorder: 'border-purple-400' },
              { bgColor: 'bg-green-50', borderColor: 'border-green-200', selectedBg: 'bg-green-100', selectedBorder: 'border-green-400' }
            ];
            const style = colors[index % colors.length] || colors[0];
            return {
              ...style,
              icon: Crown,
              iconAlt: 'Plan'
            };
          };
          
          const planStyle = getPlanStyle(index);
          
          return (
            <div 
              key={plan.id} 
              className={`relative rounded-xl border-2 p-6 transition-all duration-200 ${
                isSelected 
                  ? `${planStyle.selectedBg} ${planStyle.selectedBorder} shadow-lg` 
                  : isCurrentPlan
                  ? 'border-green-500 bg-green-50 shadow-md'
                  : hasActiveSubscription
                  ? 'border-gray-200 bg-gray-50 opacity-75'
                  : `${planStyle.bgColor} ${planStyle.borderColor} hover:shadow-md`
              }`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white px-4 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                    <Star className="h-4 w-4" />
                    <span>{t('subscriptions.plans.popular')}</span>
                  </div>
                </div>
              )}
              
              {isCurrentPlan && (
                <div className="absolute -top-3 right-4">
                  <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                    <CheckCircle className="h-4 w-4" />
                    <span>{t('subscriptions.plans.current')}</span>
                  </div>
                </div>
              )}
              
              <div className="text-center">
                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <Crown className="h-16 w-16 text-brand-primary" />
                </div>
                
                {/* Plan Name */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">{plan.title}</h3>
                
                {/* Price */}
                <div className="mb-6">
                  <div className="text-3xl font-bold text-gray-900">
                    {plan.price === 0 ? t('subscriptions.plans.free') : `${plan.price.toLocaleString()} DZD`}
                  </div>
                  <div className="text-gray-600">
                    {plan.duration === 'annual' ? t('subscriptions.plans.perYear') : plan.duration === 'monthly' ? t('subscriptions.plans.perMonth') : plan.duration}
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700">
                      {plan.listing_limit ? t('subscriptions.plans.upToServices', { count: plan.listing_limit }) : t('subscriptions.plans.unlimitedServices')}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700">{t('subscriptions.plans.prioritySupport')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700">{t('subscriptions.plans.advancedStats')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700">{t('subscriptions.plans.automaticBilling')}</span>
                  </div>
                </div>

                <button 
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                    isCurrentPlan
                      ? 'bg-green-500 text-white cursor-default'
                      : hasActiveSubscription
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : isSelected
                      ? 'bg-brand-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => !hasActiveSubscription && setSelectedPlan(plan.id)}
                 //Fevrier 2026
                 disabled={hasActiveSubscription || new Date(2026, 1, 1) > new Date()}
                >
                  {isCurrentPlan 
                    ? t('subscriptions.plans.current')
                    : hasActiveSubscription 
                    ? t('subscriptions.plans.activeSubscription')
                    : isSelected 
                    ? t('subscriptions.plans.selected')
                    : t('subscriptions.plans.choosePlan')
                  }
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Payment Upload Section */}
      {selectedPlan && currentSubscription?.status !== 'active' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <Upload className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('subscriptions.payment.title')}</h2>
              <p className="text-gray-600">{t('subscriptions.payment.subtitle')}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-900">
                  {receipt ? receipt.name : t('subscriptions.payment.selectReceipt')}
                </p>
                <p className="text-sm text-gray-500">
                  {t('subscriptions.payment.acceptedFormats')}
                </p>
                <input 
                  type="file" 
                  accept="image/*,application/pdf" 
                  onChange={(e) => setReceipt(e.target.files?.[0] || null)}
                  className="hidden"
                  id="receipt-upload"
                />
                <label 
                  htmlFor="receipt-upload"
                  className="inline-flex items-center px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90 cursor-pointer transition-colors"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {t('subscriptions.payment.chooseFile')}
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                <p>• {t('subscriptions.payment.verificationNote')}</p>
                <p>• {t('subscriptions.payment.processingTime')}</p>
              </div>
              <button 
                className="px-6 py-3 bg-brand-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                onClick={submitRequest}
                disabled={!receipt || uploading}
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{t('subscriptions.payment.uploading')}</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span>{t('subscriptions.payment.sendRequest')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantSubscriptionsPage;
